import { useEffect } from "react";

// Widget de ayuda (editor.lisual.com). Se carga una sola vez por página y, si hay sesión de
// LisualPro, se identifica solo: el bot saluda por nombre y arranca en la obra que se está mirando,
// sin pedir el WhatsApp. Sin sesión (o si algo falla) queda como siempre, pidiendo el WhatsApp.

const WIDGET_URL = process.env.REACT_APP_WIDGET_URL || "https://editor.lisual.com/widget/lisual-widget.min.js";
const WIDGET_API_BASE = process.env.REACT_APP_WIDGET_API_BASE || ""; // vacío = el default del widget (editor.lisual.com)
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8081";
// En local se puede pedir el token a otro servidor (el backend de producción todavía no tiene la ruta).
const TOKEN_URL = process.env.REACT_APP_WIDGET_TOKEN_URL || `${BACKEND_URL}/api/auth/widget-token`;
const SCRIPT_ID = "lisual-widget-script";
const TOKEN_KEY = "authLisualToken";

let lastKey = null;

const ensureScript = () => {
  if (typeof document === "undefined") return;
  if (WIDGET_API_BASE) {
    window.LisualWidgetConfig = { ...(window.LisualWidgetConfig || {}), apiBase: WIDGET_API_BASE };
  }
  if (document.getElementById(SCRIPT_ID) || window.__lisualWidgetMounted) return;
  const script = document.createElement("script");
  script.id = SCRIPT_ID;
  script.src = WIDGET_URL;
  script.async = true;
  document.body.appendChild(script);
};

// Si el widget todavía no cargó, la llamada queda en la cola y la procesa al arrancar.
export const callLisualWidget = (method, arg) => {
  if (window.LisualWidget && typeof window.LisualWidget[method] === "function") {
    window.LisualWidget[method](arg);
    return;
  }
  const q = window.LisualWidgetQueue || [];
  if (Array.isArray(q)) {
    window.LisualWidgetQueue = q.filter((it) => it[0] !== method).concat([[method, arg]]);
  } else if (q && typeof q.push === "function") {
    q.push([method, arg]);
  }
};

export const logoutLisualWidget = () => {
  if (lastKey === null) return;
  lastKey = null;
  callLisualWidget("logout");
};

// Pide al backend el token corto del widget. Usa fetch (no el axios con interceptor) para que
// un error acá nunca cierre la sesión de LisualPro.
const fetchWidgetToken = async (authToken, feedId, empresa) => {
  const res = await fetch(TOKEN_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
    // customerId = la empresa que se está mirando (un admin entra como esa empresa). Nombre y teléfono
    // sólo los usa el servidor de prueba local: el backend real los busca él mismo.
    body: JSON.stringify({ ...(feedId ? { feedId } : {}), ...(empresa && empresa.customerId ? empresa : {}) }),
  });
  if (!res.ok) throw new Error(`widget-token ${res.status}`);
  const data = await res.json();
  return data && data.token;
};

/**
 * @param {{ feedId?: string, feedName?: string, customerId?: string, customerName?: string, customerPhone?: string, ready?: boolean, getToken?: (feedId?: string) => Promise<string> }} opts
 *   customerId: la empresa que muestra la página (si un admin la mira, el widget entra como esa empresa).
 *   ready: false mientras la página todavía no sabe qué cámara muestra (evita identificar dos veces).
 *   getToken: sólo para pruebas locales (reemplaza la llamada al backend).
 */
export default function useLisualWidget({ feedId = "", feedName = "", customerId = "", customerName = "", customerPhone = "", ready = true, getToken } = {}) {
  useEffect(() => {
    ensureScript();
    if (!ready) return undefined;
    const authToken = localStorage.getItem(TOKEN_KEY);
    if (!authToken && !getToken) {
      logoutLisualWidget();
      return undefined;
    }
    const key = `${authToken || "dev"}|${feedId || ""}|${customerId || ""}`;
    if (key === lastKey) return undefined;
    let cancelled = false;
    (getToken ? getToken(feedId) : fetchWidgetToken(authToken, feedId, { customerId, customerName, customerPhone }))
      .then((token) => {
        if (cancelled || !token) return;
        lastKey = key;
        callLisualWidget("identify", { token, feedId, feedName });
      })
      .catch((e) => {
        // Sin token el widget sigue funcionando como antes (pide el WhatsApp).
        console.warn("[widget]", e.message);
      });
    return () => {
      cancelled = true;
    };
  }, [feedId, feedName, customerId, customerName, customerPhone, ready, getToken]);
}
